import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';
import { Language, TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AuthService, AuthWebsocketService, ClusterService, NetworkService, WatchdogService } from '../../services';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { transition, trigger, useAnimation } from '@angular/animations';
import { bounceIn, fadeIn, fadeOut } from 'ng-animate';
import { filter, switchMap } from 'rxjs/operators';
import { AuthJwtResponse, AuthWebSocketResponse, AuthWidgetCodeResponse } from '../../models/websocket.model';
import { isFalsy, isRecord, isTruthy } from '../../utils';

@Component({
  selector: 'auth-page',
  templateUrl: './auth-page.component.html',
  styleUrls: ['./auth-page.component.scss'],
  imports: [
    TranslatePipe,
  ],
  providers: [
    AuthWebsocketService,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('bounceIn', [
      transition(':enter', [
        useAnimation(bounceIn, {
          params: { timing: 0.4 },
        }),
      ]),
    ]),
    trigger('changeBounceIn', [
      transition('* => *', [
        useAnimation(bounceIn, {
          params: { timing: 0.4 },
        }),
      ]),
    ]),
    trigger('fadeInOut', [
      transition(':enter', [
        useAnimation(fadeIn, {
          params: { timing: 0.2 },
        }),
      ]),
      transition(':leave', [
        useAnimation(fadeOut, {
          params: { timing: 0.2 },
        }),
      ]),
    ]),
  ],
})
export class AuthPageComponent {

  private readonly destroyRef = inject(DestroyRef);
  private readonly translate = inject(TranslateService);
  private readonly network = inject(NetworkService);
  private readonly auth = inject(AuthService);
  private readonly cluster = inject(ClusterService);
  private readonly watchdog = inject(WatchdogService);
  private readonly authWebsocket = inject(AuthWebsocketService);

  private readonly logger = this.watchdog.tag('Auth Page', 'magenta');

  public readonly online = toSignal(this.network.status$, {
    initialValue: this.network.isOnline,
  });

  public readonly code = signal<string | null>(null);

  constructor() {
    this.authWebsocket.initialize();

    this.cluster.leader$.pipe(
      filter(isTruthy),
      switchMap(() => this.authWebsocket.status$),
      filter(isFalsy),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      this.code.set(null);
      this.cluster.broadcast('auth.code', null);
    });

    this.cluster.leader$.pipe(
      filter(isTruthy),
      switchMap(() => this.authWebsocket.messages$),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((message) => {
      this.wsOnMessage(message);
    });

    this.cluster.messages$.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((message) => {
      if (message.type === 'auth.code') {
        this.code.set(message.data);
      }

      if (message.type === 'member.new' && this.cluster.leader) {
        this.cluster.send(message.from, 'auth.code', this.code()).then(() => {
          this.logger.debug('Send code to new member', message.from);
        });
      }
    });
  }

  get currentLanguage(): Language {
    return this.translate.getCurrentLang();
  }

  get availableLanguages(): ReadonlyArray<Language> {
    return this.translate.getLangs();
  }

  public handlerChangeLanguage(event: Event): void {
    if (event.target instanceof HTMLSelectElement) {
      this.translate.use(event.target.value);
    }
  }

  private wsOnMessage(response: AuthWebSocketResponse): void {
    switch (response.type) {
      case 'widgetCode':
        this.wsOnWidgetCode(response);
        break;

      case 'jwt':
        this.wsOnJwt(response);
        break;

      case 'heartbeat':
        this.logger.debug('Heartbeat received', this.getHeartbeatTime(response));
        break;

      default:
        this.logger.warn('Unknown message', response);
        break;
    }
  }

  private wsOnWidgetCode(response: AuthWebSocketResponse): void {
    if (!this.isWidgetCodeResponse(response)) {
      this.logger.warn('Invalid widget code message', response);
      return;
    }

    this.code.set(response.data.widgetCode);
    this.logger.debug('Widget code received', response.data.widgetCode);
    this.cluster.broadcast('auth.code', response.data.widgetCode);
  }

  private wsOnJwt(response: AuthWebSocketResponse): void {
    if (!this.isJwtResponse(response)) {
      this.logger.warn('Invalid token message', response);
      return;
    }

    this.auth.login(response.data.JWT);
    this.authWebsocket.complete();
    this.logger.debug('Token received');
  }

  private isWidgetCodeResponse(response: AuthWebSocketResponse): response is AuthWidgetCodeResponse {
    return (
      response.type === 'widgetCode'
      && isRecord(response.data)
      && typeof response.data['widgetCode'] === 'string'
    );
  }

  private isJwtResponse(response: AuthWebSocketResponse): response is AuthJwtResponse {
    return (
      response.type === 'jwt'
      && isRecord(response.data)
      && typeof response.data['JWT'] === 'string'
    );
  }

  private getHeartbeatTime(response: AuthWebSocketResponse): unknown {
    if (!isRecord(response.data)) {
      return undefined;
    }

    return response.data['time'];
  }

}
